/**
 * webrtc.js — thin browser WebRTC peer wrapper for DocSite voice calls.
 *
 * Exposes a single class `VoicePeer` that wires:
 *   - getUserMedia (audio only)
 *   - RTCPeerConnection (with caller-supplied ICE servers)
 *   - SDP offer/answer + ICE candidate exchange via signalling callbacks
 *   - lifecycle events (onState / onRemoteStream / onError)
 *
 * The caller is responsible for transporting the signalling messages to/from
 * the peer (HTTP polling against /api/voice/call/{id}/signal in our case).
 */
export class VoicePeer {
  constructor({ role, iceServers, onState, onRemoteStream, onError, onLocalSignal }) {
    this.role = role; // 'caller' (patient) | 'callee' (doctor)
    this.iceServers = iceServers || [{ urls: ['stun:stun.l.google.com:19302'] }];
    this.onState = onState || (() => {});
    this.onRemoteStream = onRemoteStream || (() => {});
    this.onError = onError || (() => {});
    this.onLocalSignal = onLocalSignal || (() => {});

    this.pc = null;
    this.localStream = null;
    this.closed = false;
    this.pendingRemoteIce = [];
  }

  async _ensureMic() {
    if (this.localStream) return this.localStream;
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      return this.localStream;
    } catch (e) {
      this.onError(`Microphone access denied: ${e.message || e.name}`);
      throw e;
    }
  }

  _newPC() {
    const pc = new RTCPeerConnection({ iceServers: this.iceServers, iceTransportPolicy: 'all' });
    pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        this.onLocalSignal({ type: 'ice', payload: ev.candidate.toJSON() });
      }
    };
    pc.ontrack = (ev) => {
      const [remote] = ev.streams;
      if (remote) this.onRemoteStream(remote);
    };
    pc.onconnectionstatechange = () => {
      this.onState(pc.connectionState);
      if (pc.connectionState === 'failed') {
        this.onError('Peer connection failed');
      }
    };
    pc.oniceconnectionstatechange = () => {
      // Helpful for debugging in the browser console
      // console.log('ICE:', pc.iceConnectionState);
    };
    return pc;
  }

  async start() {
    if (this.pc) return;
    await this._ensureMic();
    this.pc = this._newPC();
    this.localStream.getTracks().forEach((t) => this.pc.addTrack(t, this.localStream));
    if (this.role === 'caller') {
      const offer = await this.pc.createOffer({ offerToReceiveAudio: true });
      await this.pc.setLocalDescription(offer);
      this.onLocalSignal({ type: 'offer', payload: { sdp: offer.sdp, type: offer.type } });
    }
  }

  async receiveSignal({ type, payload }) {
    if (!this.pc) {
      // Defer ICE until after PC is built
      if (type === 'ice') this.pendingRemoteIce.push(payload);
      return;
    }
    try {
      if (type === 'offer') {
        await this.pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
        for (const c of this.pendingRemoteIce) {
          try { await this.pc.addIceCandidate(c); } catch {}
        }
        this.pendingRemoteIce = [];
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        this.onLocalSignal({ type: 'answer', payload: { sdp: answer.sdp, type: answer.type } });
      } else if (type === 'answer') {
        await this.pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
        for (const c of this.pendingRemoteIce) {
          try { await this.pc.addIceCandidate(c); } catch {}
        }
        this.pendingRemoteIce = [];
      } else if (type === 'ice') {
        if (this.pc.remoteDescription) {
          try { await this.pc.addIceCandidate(payload); } catch {}
        } else {
          this.pendingRemoteIce.push(payload);
        }
      }
    } catch (e) {
      this.onError(`Signal handling failed: ${e.message || e}`);
    }
  }

  async startCallee() {
    // Doctor side: pre-create peer + get mic so the offer arrives to a ready PC
    if (this.pc) return;
    await this._ensureMic();
    this.pc = this._newPC();
    this.localStream.getTracks().forEach((t) => this.pc.addTrack(t, this.localStream));
  }

  setMicMuted(muted) {
    if (!this.localStream) return;
    this.localStream.getAudioTracks().forEach((t) => (t.enabled = !muted));
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    try { this.pc?.close(); } catch {}
    try { this.localStream?.getTracks().forEach((t) => t.stop()); } catch {}
    this.pc = null;
    this.localStream = null;
  }
}

export function bindRemoteAudio(audioEl, stream) {
  if (!audioEl || !stream) return;
  audioEl.srcObject = stream;
  audioEl.autoplay = true;
  audioEl.play().catch(() => {});
}
